import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { BUNDLED, layerUrl, thumbUrl } from "./tiwiAssets";

// The try-on stage: a pixel-faithful replica of the app's Doggo room, with
// every catalog item — LIVE, hidden, dig-only, whatever — equippable per
// slot. This is the "fitting room" that lets art be judged in combination
// (beanie + raincoat + bandana, full room set) BEFORE anything goes live.
//
// Fidelity contract (mirrors the Flutter widgets exactly — if the app's
// rules change, change these too):
//   - Room canvas 1416x1054; paint order wall, plant, rug, bed, toy
//     (TiwiSlots.room), mascot drawn last so furniture never hides it.
//   - Mascot: square canvas, 66% of room height, bottom-centred (TiwiRoom).
//   - Worn order: base, then costume — a costume REPLACES top and neck —
//     otherwise top then neck, then hat on top (TiwiAvatar). An empty neck
//     slot wears the classic collar, same as the app.
//
// Art resolves like the app's TiwiArt: server-hosted layer_image when the
// row has one (admin-uploaded), bundled asset by asset_key otherwise
// (launch items — copied from the app bundle into src/Assets/tiwi).

const WORN_SLOTS = ["hat", "top", "neck", "costume"];
const ROOM_PAINT_ORDER = ["wall", "plant", "rug", "bed", "toy"];
// The frame borders the whole card, ABOVE the mascot (transparent centre
// by art contract) — drawn last, mirroring TiwiRoom.
const ALL_SLOTS = [...WORN_SLOTS, ...ROOM_PAINT_ORDER, "frame"];

const ROOM_W = 1416;
const ROOM_H = 1054;

const fullLayer = (src, key, zIndex) =>
  src ? (
    <img
      key={key}
      src={src}
      alt=""
      draggable={false}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "contain",
        pointerEvents: "none",
        zIndex,
      }}
    />
  ) : null;

const TryOnStage = forwardRef(function TryOnStage({ rows }, ref) {
  const [expanded, setExpanded] = useState(true);
  // slot -> catalog row (or undefined = empty)
  const [fit, setFit] = useState({});

  const bySlot = useMemo(() => {
    const m = Object.fromEntries(ALL_SLOTS.map((s) => [s, []]));
    for (const r of rows) if (m[r.slot]) m[r.slot].push(r);
    return m;
  }, [rows]);

  useImperativeHandle(ref, () => ({
    // Row-level "try on": equip into the item's own slot and open the stage.
    tryOn(item) {
      setExpanded(true);
      setFit((f) => ({ ...f, [item.slot]: item }));
      document
        .getElementById("tryon-stage-box")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
  }));

  const equip = (slot, id) =>
    setFit((f) => ({
      ...f,
      [slot]: bySlot[slot].find((r) => r.id === id) || undefined,
    }));

  // ---- replica composition (see fidelity contract above) -----------------
  // An empty neck renders NOTHING — bare is the app's default look now
  // (2026-08-17; the classic-collar fallback is gone). covers_neck tops
  // suppress an equipped collar, exactly as the app does.
  const wearingCostume = !!fit.costume;
  const neckSuppressed = fit.top?.covers_neck === 1;
  const avatarLayers = [
    fullLayer(BUNDLED.tiwi_base, "base", 1),
    ...(wearingCostume
      ? [fullLayer(layerUrl(fit.costume), "costume", 2)]
      : [
          fullLayer(layerUrl(fit.top), "top", 2),
          neckSuppressed ? null : fullLayer(layerUrl(fit.neck), "neck", 3),
        ]),
    fullLayer(layerUrl(fit.hat), "hat", 4),
  ];

  const stageW = 560;
  const stageH = Math.round((stageW * ROOM_H) / ROOM_W);
  const avatarH = Math.round(stageH * 0.66);

  const missingArt = ALL_SLOTS.map((s) => fit[s])
    .filter((it) => it && !layerUrl(it))
    .map((it) => it.name);

  const slotPicker = (slot) => (
    <TextField
      key={slot}
      select
      size="small"
      label={slot}
      value={fit[slot]?.id ?? ""}
      onChange={(e) => equip(slot, e.target.value)}
      sx={{ minWidth: 168 }}
    >
      <MenuItem value="">
        <em>empty</em>
      </MenuItem>
      {bySlot[slot].map((r) => (
        <MenuItem key={r.id} value={r.id}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
            {thumbUrl(r) ? (
              <img
                src={thumbUrl(r)}
                alt=""
                style={{ width: 22, height: 22, objectFit: "contain" }}
              />
            ) : (
              <Box sx={{ width: 22, height: 22 }} />
            )}
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>
              {r.name}
            </Typography>
            {r.status !== 1 && <Chip label="hidden" size="small" sx={{ height: 16 }} />}
            {!layerUrl(r) && (
              <Chip label="no art" size="small" color="warning" sx={{ height: 16 }} />
            )}
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, v) => setExpanded(v)}
      disableGutters
      sx={{ mb: 2, border: "1px solid #e0e0e0", borderRadius: "10px !important", "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CheckroomIcon sx={{ color: "#1d3010" }} />
          <Box>
            <Typography fontWeight={700}>Try-On Stage</Typography>
            <Typography variant="caption" color="text.secondary">
              A replica of the in-app Doggo home — equip any item (live or hidden) and judge
              the full look before releasing it
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="flex-start">
          {/* the stage. A frame STRADDLES the room edge (2026-08-20):
              the room insets by half the bezel so the shell's centreline
              runs along the old room outline — mirrors TiwiRoom. */}
          <Box
            id="tryon-stage-box"
            sx={{
              position: "relative",
              width: stageW,
              height: stageH,
              maxWidth: "100%",
              borderRadius: 3,
              overflow: "hidden",
              flexShrink: 0,
              boxShadow: "0 6px 24px rgba(0,0,0,.12)",
              bgcolor: "#f2ebe3",
            }}
          >
           <Box sx={{ position: "absolute", inset: fit.frame ? `${(stageW * (74 / 1416)) / 2}px` : 0 }}>
            {fullLayer(BUNDLED.room_default, "room", 0)}
            {ROOM_PAINT_ORDER.map((slot, i) =>
              fullLayer(layerUrl(fit[slot]), slot, i + 1)
            )}
            {/* mascot: square, 66% height, centred, lifted 3.4% off the
              bottom — TiwiRoom's math (mascotAlignY = 0.8; change both) */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                bottom: Math.round(stageH * 0.034),
                transform: "translateX(-50%)",
                width: avatarH,
                height: avatarH,
                zIndex: 10,
              }}
            >
              {avatarLayers}
            </Box>
           </Box>
            {fullLayer(layerUrl(fit.frame), "frame", 20)}
          </Box>

          {/* the wardrobe controls */}
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Worn
            </Typography>
            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
              {WORN_SLOTS.map(slotPicker)}
            </Stack>
            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
              Room
            </Typography>
            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
              {[...ROOM_PAINT_ORDER, "frame"].map(slotPicker)}
            </Stack>
            {wearingCostume && (fit.top || fit.neck) && (
              <Typography variant="caption" color="text.secondary">
                Costume equipped — it replaces the top and collar on the model, exactly as in
                the app. The underlying picks stay selected.
              </Typography>
            )}
            {missingArt.length > 0 && (
              <Chip
                color="warning"
                size="small"
                sx={{ alignSelf: "flex-start" }}
                label={`No art yet, nothing to draw: ${missingArt.join(", ")}`}
              />
            )}
            <Box>
              <Tooltip title="Clear every slot">
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => setFit({})}
                  sx={{ color: "#1d3010" }}
                >
                  Reset
                </Button>
              </Tooltip>
            </Box>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
});

export default TryOnStage;
